// @ts-strict-ignore
import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import type { Action, ThresholdResults } from '~/dashboard/views/workflows/store/engine/types';
import {
  selectLegacyMappings,
  selectWorkflowData,
} from '~/dashboard/views/workflows/store/redux/selectors';
import { UserContext } from '~/dashboard/context';
import type { Dimension } from '~/application-review/redux/types';
import { EditorContainerType } from 'flow/store/redux/constants';
import {
  setDimensions,
  setEditorContainerType,
  setEntityToken,
  setEvaluationTagNames,
  setEvaluationToken,
  setFocusedNodeId,
  setThresholdOutputResults,
  setEvaluationOutcome,
  setIsRuleExplainabilityPortfolioEvaluation,
  setMatrixModelResults,
} from 'flow/store/redux/slice';
import apiCalls from '~/helpers/apiCalls';
import { useInitializeWorkflowRedux } from 'flow/editor/hooks/useInitializeWorkflowRedux';
import { useThresholdResults } from './hooks';
import { reversedObjectKeyVal } from './utils';

type UseRuleExplainabilityArgs = {
  entityToken: string;
  evaluationToken: string;
  evaluationTags: Array<{ tag_name: string }>;
  isPortfolioEvaluation: boolean;
  workflowToken: string | null;
  workflowVersion: number | null;
  dimensions?: Dimension[];
  outcome?: string | null;
  /** When provided, focuses the matching node and loads its threshold results. */
  actionName?: string;
  /** Editor container the read-only canvas renders inside (modal vs full page). */
  containerType?: EditorContainerType;
};

// Defaulting dimensions to a new array inline causes an infinite update loop in some cases.
// Defaulting to a pre-defined initialDimensions fixes this issue.
const initialDimensions: Dimension[] = [];

/**
 * Loads the data and hydrates the Redux state needed to render the read-only
 * workflow editor for a single evaluation ("rule explainability"). Extracted
 * from TagRuleModal so both the modal and the standalone explainability page
 * can share one implementation.
 */
export const useRuleExplainability = ({
  entityToken,
  evaluationToken,
  evaluationTags,
  isPortfolioEvaluation,
  workflowToken,
  workflowVersion,
  dimensions = initialDimensions,
  outcome = null,
  actionName,
  containerType = EditorContainerType.MODAL,
}: UseRuleExplainabilityArgs) => {
  const { permissions, featureFlags } = useContext(UserContext);

  const canViewRules = !!permissions['READ:ApplicationVersions:null'];

  const dispatch = useAppDispatch();
  const workflowData = useAppSelector(selectWorkflowData);

  const shouldInitializeWorkflow = useMemo(() => Boolean(
    workflowToken
    && workflowVersion
    && (
      !workflowData?.token
      || !workflowData?.publicVersion
      || workflowData?.token !== workflowToken
      || workflowData?.publicVersion !== workflowVersion
    ),
  ), [workflowData?.token, workflowData?.publicVersion, workflowToken, workflowVersion]);

  useInitializeWorkflowRedux({
    workflowToken,
    versionId: workflowVersion,
    featureFlags,
    enabled: shouldInitializeWorkflow,
    reinitialize: true,
  });

  const legacyMappings = useAppSelector(selectLegacyMappings);
  const [actionToShow, setActionToShow] = useState<Action>(null);
  const [thresholdResults, setThresholdResults] = useState<ThresholdResults>(null);
  const [
    isRuleExplainabilityDataLoaded,
    setIsRuleExplainabilityDataLoaded,
  ] = useState<boolean>(false);

  useQuery({
    queryKey: ['getEvaluation', entityToken, evaluationToken],
    queryFn: async () => {
      const response = await apiCalls.entities.getEvaluation({ entityToken, evaluationToken });
      const matrixModelResults = response?.event?.final_response?.summary?.matrix_models;
      dispatch(setMatrixModelResults(matrixModelResults));
      return matrixModelResults;
    },
  });

  const workflowLoading = useMemo(() => !workflowData?.actions, [workflowData]);

  /**
   * initWorkflowPropsAndData (called by useInitializeWorkflowRedux) resets workflowFeatures,
   * which will overwrite existing tokens, so we wait until the workflow finishes loading
   * before setting tokens.
   */
  useEffect(() => {
    if (!workflowLoading) {
      dispatch(setEntityToken(entityToken));
      dispatch(setEvaluationToken(evaluationToken));
      dispatch(setDimensions(dimensions));
    }
  }, [workflowLoading, dimensions, dispatch, entityToken, evaluationToken]);

  useEffect(() => {
    // These overlays live in workflowFeatures too, so — like the token effect above — we
    // must wait until the workflow finishes loading (and re-apply if it re-initializes).
    // Otherwise a cold load (e.g. the standalone explainability page) dispatches them
    // before init wipes them, so the set tag node indicators and outcome never render.
    if (workflowLoading) return;
    dispatch(setEvaluationTagNames((evaluationTags || []).map(({ tag_name }) => tag_name)));
    if (outcome) dispatch(setEvaluationOutcome(outcome));
  }, [workflowLoading, dispatch, evaluationTags, outcome]);

  const {
    data: thresholdResultsData,
    isLoading: thresholdResultsLoading,
    isError: thresholdResultsError,
  } = useThresholdResults({
    entityToken,
    evaluationToken,
    canViewRules,
    isPortfolioEvaluation,
    dimensions,
  });

  const reversedActionMappings = reversedObjectKeyVal(legacyMappings?.actions || {});

  const legacyActionId = useMemo(() => (
    reversedActionMappings?.[actionToShow?.publicId] || null
  ), [actionToShow, reversedActionMappings]);

  const targetAction = useMemo(() => {
    const actionValues = Object.values(workflowData?.actions ?? {});
    if (actionName) {
      return actionValues.find(actionValue => (
        actionValue?.tag?.name === actionName
        || actionValue?.matrixModel?.name === actionName
        || actionValue?.input?.name === actionName
      ));
    }
    return null;
  }, [actionName, workflowData?.actions]);

  useEffect(() => {
    if (targetAction && !workflowLoading) {
      dispatch(setFocusedNodeId(targetAction.publicId));
      dispatch(setEditorContainerType(containerType));
      setActionToShow(targetAction);
    }
  }, [workflowLoading, dispatch, targetAction, containerType]);

  useEffect(() => {
    // Always process threshold results when available, even if from cache
    // This ensures the effect runs on remount with cached data
    if (actionToShow && legacyMappings && thresholdResultsData) {
      // eslint-disable-next-line no-underscore-dangle
      const resultsData = thresholdResultsData?._embedded;
      const thresholdResultsByAction = resultsData?.actions?.[legacyActionId];

      if (thresholdResultsByAction) {
        setThresholdResults({
          ...thresholdResultsByAction,
          is_portfolio_evaluation: isPortfolioEvaluation,
        });
        dispatch(setThresholdOutputResults(resultsData?.actions));
        dispatch(setIsRuleExplainabilityPortfolioEvaluation(Boolean(isPortfolioEvaluation)));
      }
    }

    // Full-workflow view (no focused action, e.g. the standalone explainability page):
    // overlay every node with its threshold results. The focused-action branch above
    // only dispatches when an actionName is supplied (the modal case).
    if (!actionName) {
      // eslint-disable-next-line no-underscore-dangle
      const allActionResults = thresholdResultsData?._embedded?.actions;
      if (allActionResults) {
        dispatch(setThresholdOutputResults(allActionResults));
        dispatch(setIsRuleExplainabilityPortfolioEvaluation(Boolean(isPortfolioEvaluation)));
      }
      setIsRuleExplainabilityDataLoaded(true);
      return;
    }

    // Focused view: mark ready once the threshold query has settled — including
    // empty/error responses — so RuleExplainabilityView / TagRuleModal don't spin forever.
    if (!thresholdResultsLoading) {
      setIsRuleExplainabilityDataLoaded(true);
    }
  }, [
    actionName,
    actionToShow,
    thresholdResultsData,
    thresholdResultsLoading,
    legacyMappings,
    legacyActionId,
    isPortfolioEvaluation,
    dispatch,
  ]);

  return {
    workflowData,
    canViewRules,
    shouldInitializeWorkflow,
    workflowLoading,
    actionToShow,
    legacyActionId,
    thresholdResults,
    thresholdResultsLoading,
    thresholdResultsError,
    isRuleExplainabilityDataLoaded,
  };
};
